<template>
  <!-- Epic D45: one blocking dialog per app while a listing is frozen for
       this tab. Its single action reloads; nothing is saved meanwhile.
       LB-26 P1-1: it lists the user's unsaved values ("Your changes",
       by field label) with Copy my changes, what the server holds now for
       those fields ("Now saved", from the 409), and asks once before a
       Reload discards listed changes. -->
  <div class="listing-frozen-root">
  <div
    v-if="homeId != null"
    class="listing-frozen-overlay"
    data-test="listing-frozen-banner"
  >
    <div
      ref="dialog"
      class="listing-frozen-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="listing-frozen-title"
      aria-describedby="listing-frozen-body"
      @keydown.tab="trapFocus"
      @keydown.esc.prevent="onEscape"
    >
      <h2 id="listing-frozen-title" class="listing-frozen-title">
        {{ copy.title }}
      </h2>
      <div class="listing-frozen-body">
        <p id="listing-frozen-body">{{ copy.body }}</p>
        <div v-if="unsaved.length" data-test="listing-frozen-unsaved">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <h3 class="listing-frozen-group-title">{{ copy.your_changes }}</h3>
            <button
              type="button"
              class="btn btn-outline-primary listing-frozen-copy"
              data-test="listing-frozen-copy"
              @click="copyChanges"
            >
              {{ copy.copy }}
            </button>
          </div>
          <section
            v-for="entry in unsaved"
            :key="entry.section"
            class="listing-frozen-section"
          >
            <h4 class="listing-frozen-section-title">{{ entry.label }}</h4>
            <textarea
              class="listing-frozen-text form-control"
              readonly
              :rows="Math.min(8, entry.text.split('\n').length + 1)"
              :aria-label="unsavedLabel(entry.label)"
              :value="entry.text"
              data-test="listing-frozen-unsaved-text"
            />
          </section>
          <section
            v-if="nowSaved"
            class="listing-frozen-section"
            data-test="listing-frozen-now-saved"
          >
            <h3 class="listing-frozen-group-title">{{ copy.now_saved }}</h3>
            <textarea
              class="listing-frozen-text form-control"
              readonly
              :rows="Math.min(8, nowSaved.split('\n').length + 1)"
              :aria-label="copy.now_saved"
              :value="nowSaved"
            />
          </section>
        </div>
        <p v-else class="listing-frozen-muted" data-test="listing-frozen-nothing">
          {{ copy.nothing }}
        </p>
        <p v-if="error" class="text-danger" role="alert" data-test="listing-frozen-error">
          {{ error }}
        </p>
      </div>
      <div
        v-if="confirming"
        class="listing-frozen-footer listing-frozen-confirm"
        data-test="listing-frozen-confirm"
      >
        <div class="listing-frozen-confirm-text">
          <p id="listing-frozen-confirm-title" class="font-weight-bold mb-1">
            {{ copy.confirm_title }}
          </p>
          <p class="mb-0">{{ copy.confirm_body }}</p>
        </div>
        <div class="listing-frozen-actions">
          <button
            ref="keep"
            type="button"
            class="btn btn-outline-secondary listing-frozen-action"
            data-test="listing-frozen-keep"
            @click="cancelConfirm"
          >
            {{ copy.confirm_cancel }}
          </button>
          <button
            type="button"
            class="btn btn-danger listing-frozen-action"
            data-test="listing-frozen-confirm-reload"
            @click="reload(true)"
          >
            {{ copy.confirm_ok }}
          </button>
        </div>
      </div>
      <div v-else class="listing-frozen-footer">
        <!-- A plain button: BButton overrides aria-disabled. It stays
             focusable while reloading (focus never leaves the dialog);
             clicks are ignored by reload(). -->
        <button
          ref="reload"
          type="button"
          class="btn btn-primary listing-frozen-action"
          :aria-disabled="reloading ? 'true' : 'false'"
          :aria-busy="reloading ? 'true' : 'false'"
          data-test="listing-frozen-reload"
          @click="reload(false)"
        >
          {{ reloading ? copy.reloading : copy.reload }}
        </button>
      </div>
    </div>
  </div>
  <!-- Outside the dialog so it still speaks after the dialog closes. -->
  <div class="sr-only" aria-live="polite" role="status" data-test="listing-frozen-status">
    {{ announcement }}
  </div>
  </div>
</template>

<script>
import en from "@/locales/en.json";
import { forgetHomeVersion, getHomeVersion } from "@/services/backend";
import {
  freezeState,
  runListingReloaders,
  unfreezeHome,
} from "@/services/listing-freeze";
import {
  collectUnsaved,
  conflictSummary,
  discardPendingForHome,
  suppressLeavePrompt,
  unsavedState,
} from "@/services/listing-save";

// LB-26 R2-A: "Go back" (not "Keep my changes": Reload is what discards
// them; this button only returns to the list).
const FROZEN_COPY = {
  ...((en && en.lb && en.lb.save && en.lb.save.frozen) || {}),
  ...((en && en.lb && en.lb.r2a && en.lb.r2a.frozen) || {}),
};

// A Reload that has not finished by then is reported as failed (the
// listing stays frozen with its edits).
export const RELOAD_TIMEOUT_MS = 20000;

function withTimeout(promise, ms) {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("reload timed out")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

const SECTION_LABELS = {
  description: "Description",
  details: "Details",
  uploads: "Uploads",
  kpis: "Key metrics",
  structure: "Investment information",
  top_metrics: "Top metrics",
  at_a_glance: "At a glance",
  tab_order: "Tab order",
  faqs: "FAQs",
  paperwork: "Paperwork (filings and risks)",
  // LB-26 R2-5: typed filings the format check holds back (never sent).
  paperwork_held: "Filings not saved yet (fix the format to save)",
  equity: "Equity details",
  terms: "Offering terms",
  incentive_surfaces: "Where incentives appear",
  admin: "Admin editor",
  company: "Company information",
  profile: "Your profile",
  // Admin autosave editors (section = component tag).
  "admin-home-reasons": "Reasons to invest (admin)",
  AdminHomeReasons: "Reasons to invest (admin)",
  "admin-home-disclosures": "Material risks / disclosures (admin)",
  AdminHomeDisclosures: "Material risks / disclosures (admin)",
  "admin-home-agreement-template": "Subscription agreement (admin)",
  AdminHomeAgreementTemplate: "Subscription agreement (admin)",
};

export default {
  name: "ListingFrozenBanner",
  data() {
    return {
      unsaved: [],
      nowSaved: "",
      reloading: false,
      confirming: false,
      error: "",
      announcement: "",
      copy: FROZEN_COPY,
    };
  },
  computed: {
    homeId() {
      const ids = Object.keys(freezeState.frozen);
      return ids.length ? ids[0] : null;
    },
    // LB-26 P1-1: bumped whenever a saver's unsaved values change.
    unsavedRevision() {
      return unsavedState.revision;
    },
  },
  watch: {
    homeId: {
      immediate: true,
      handler(id) {
        this.error = "";
        this.confirming = false;
        this.lockScroll(id != null);
        if (id == null) return;
        this.announcement = "";
        // Captured after the focus moves (so a CKEditor that just lost
        // focus has flushed), and again inside reload() before anything is
        // discarded.
        this.$nextTick(() => {
          this.focusReload();
          this.captureUnsaved(id);
        });
      },
    },
  },
  beforeDestroy() {
    this.lockScroll(false);
  },
  created() {
    // Re-capture while frozen whenever what the savers hold changes (a
    // failed send restoring its keys, a late keystroke, a settle).
    this.$watch("unsavedRevision", () => {
      if (this.homeId != null && !this.reloading) this.captureUnsaved(this.homeId);
    });
  },
  methods: {
    captureUnsaved(id) {
      const seen = new Set();
      this.unsaved = collectUnsaved(id)
        .map((entry) => ({
          ...entry,
          label: SECTION_LABELS[entry.section] || entry.section,
        }))
        .filter((entry) => {
          const key = `${entry.section}\u0000${entry.text}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      const keys = [];
      this.unsaved.forEach((entry) => (entry.keys || []).forEach((k) => keys.push(k)));
      this.nowSaved = this.unsaved.length ? conflictSummary(id, keys) : "";
      if (!this.unsaved.length) this.confirming = false;
    },
    unsavedLabel(label) {
      return String(this.copy.unsaved_label || "Unsaved {section}").replace(
        "{section}",
        label
      );
    },
    copyText() {
      return this.unsaved
        .map((entry) => `${entry.label}\n${entry.text}`)
        .join("\n\n");
    },
    // "Copy my changes": the clipboard API, else a selected textarea.
    async copyChanges() {
      const text = this.copyText();
      let ok = false;
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          ok = true;
        }
      } catch (_) {
        ok = false;
      }
      if (!ok) ok = this.legacyCopy(text);
      this.announcement = "";
      this.$nextTick(() => {
        this.announcement = ok ? this.copy.copied : this.copy.copy_failed;
      });
    },
    legacyCopy(text) {
      if (typeof document === "undefined" || !document.body) return false;
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      const dialog = this.$refs.dialog || document.body;
      dialog.appendChild(area);
      area.select();
      let ok = false;
      try {
        ok = typeof document.execCommand === "function" && document.execCommand("copy");
      } catch (_) {
        ok = false;
      }
      dialog.removeChild(area);
      this.focusCopy();
      return Boolean(ok);
    },
    focusCopy() {
      const el = this.$el && this.$el.querySelector && this.$el.querySelector('[data-test="listing-frozen-copy"]');
      if (el && typeof el.focus === "function") el.focus();
    },
    onEscape() {
      if (this.confirming) this.cancelConfirm();
    },
    cancelConfirm() {
      this.confirming = false;
      this.$nextTick(() => this.focusReload());
    },
    // iOS-safe lock: fix the body in place (keeping the scroll position)
    // and restore both on close.
    lockScroll(on) {
      if (typeof document === "undefined" || !document.body) return;
      const body = document.body;
      if (on && !this._scrollLocked) {
        this._scrollLocked = true;
        this._scrollY = typeof window !== "undefined" ? window.pageYOffset || 0 : 0;
        body.classList.add("listing-frozen-scroll-lock");
        body.style.position = "fixed";
        body.style.top = `-${this._scrollY}px`;
        body.style.width = "100%";
      } else if (!on && this._scrollLocked) {
        this._scrollLocked = false;
        body.classList.remove("listing-frozen-scroll-lock");
        body.style.position = "";
        body.style.top = "";
        body.style.width = "";
        if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
          try {
            window.scrollTo(0, this._scrollY || 0);
          } catch (_) {
            // jsdom / old browsers
          }
        }
      }
    },
    // After a successful Reload: focus the page heading and announce it.
    afterReload() {
      this.announcement = this.copy.reloaded;
      this.$nextTick(() => {
        if (typeof document === "undefined") return;
        const visible = (el) => {
          if (!el || el.closest("[hidden]")) return false;
          const style = window.getComputedStyle(el);
          return style.display !== "none" && style.visibility !== "hidden";
        };
        // main h1 -> first visible h1 / h2 in the app -> the app itself.
        const target =
          Array.from(document.querySelectorAll("main h1")).find(visible) ||
          Array.from(document.querySelectorAll("#app h1, #app h2")).find(visible) ||
          document.querySelector("#app");
        if (target) {
          if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
          target.focus();
        }
      });
    },
    focusReload() {
      const button = this.$refs.reload;
      const el = button && (button.$el || button);
      if (el && typeof el.focus === "function") el.focus();
    },
    trapFocus(event) {
      const dialog = this.$refs.dialog;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll("button, textarea, [tabindex]:not([tabindex='-1'])")
      ).filter((el) => !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    // Reload: re-fetch for editing and rehydrate the page once (every
    // mounted page that shows this listing), then drop the unsaved edits
    // and unfreeze — only if the reload really loaded the listing (its
    // version is known again). Nothing on screen can rehydrate -> full
    // page reload. A failed or slow (> 20 s) reload stays frozen with the
    // edits kept.
    // HCD-11: with changes listed, Reload asks once ("Reload and discard
    // these changes?"); `confirmed` is that answer.
    async reload(confirmed) {
      const id = this.homeId;
      if (id == null || this.reloading) return;
      // Last look at what is on screen before anything is dropped.
      this.captureUnsaved(id);
      if (this.unsaved.length && confirmed !== true) {
        this.confirming = true;
        this.$nextTick(() => {
          const keep = this.$refs.keep;
          if (keep && typeof keep.focus === "function") keep.focus();
        });
        return;
      }
      this.confirming = false;
      this.reloading = true;
      this.error = "";
      try {
        forgetHomeVersion(id);
        const handled = await withTimeout(runListingReloaders(id), RELOAD_TIMEOUT_MS);
        if (!handled) {
          discardPendingForHome(id);
          this.hardReload(id);
          return;
        }
        if (getHomeVersion(id) === undefined) {
          throw new Error("reload did not load the listing");
        }
        discardPendingForHome(id);
        unfreezeHome(id);
        this.afterReload();
      } catch (_) {
        this.error = this.copy.reload_failed;
      } finally {
        this.reloading = false;
      }
    },
    hardReload(homeId) {
      // This listing's edits were shown and discarded on purpose: no
      // "leave site?" for them (other listings' edits still prompt).
      suppressLeavePrompt(homeId);
      window.location.reload();
    },
  },
};
</script>

<style lang="scss">
body.listing-frozen-scroll-lock {
  overflow: hidden;
}
/* The issuer wizard steps wrap their form in a fieldset that is disabled
   while the listing is frozen; it must not change the layout. */
.lyp-frozen-fieldset {
  display: contents;
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
/* R3-16 (WCAG 2.5.8): a builder radio / checkbox label is at least 24px
   tall, so the whole row is a 24px target (Bootstrap's is 21px). */
.lyp-frozen-fieldset .custom-radio .custom-control-label,
.lyp-frozen-fieldset .custom-checkbox .custom-control-label {
  min-height: 24px;
  line-height: 24px;
}
/* DS-6: the blocking-dialog tier, above Bootstrap modals (1050), the admin
   agreement workspace (1060), sticky bars and the cookie banner. */
.listing-frozen-overlay {
  position: fixed;
  inset: 0;
  overscroll-behavior: contain;
  z-index: 1080;
  z-index: var(--z-blocking-dialog, 1080);
  background: rgba($gray-900, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.listing-frozen-dialog {
  background: $white;
  border-radius: $border-radius;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.listing-frozen-title {
  font-size: $h5-font-size;
  margin: 0;
  padding: 16px 16px 8px;
}
.listing-frozen-group-title {
  font-size: $font-size-base;
  font-weight: 600;
  margin: 12px 0 4px;
}
.listing-frozen-muted {
  color: $gray-700;
}
.listing-frozen-copy {
  min-height: 44px;
  margin: 8px 0 4px;
}
.listing-frozen-confirm {
  flex-direction: column;
  align-items: stretch;
  border-top: 1px solid $gray-300;
}
.listing-frozen-confirm-text {
  margin-bottom: 8px;
}
.listing-frozen-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.listing-frozen-body {
  padding: 0 16px;
  overflow-y: auto;
  flex: 1 1 auto;
}
.listing-frozen-section-title {
  font-size: $font-size-base;
  font-weight: 500;
  color: $gray-700;
  margin: 8px 0 4px;
}
.listing-frozen-text {
  white-space: pre-wrap;
  user-select: text;
}
.listing-frozen-footer {
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}
.listing-frozen-action {
  min-height: 44px;
  min-width: 120px;
}
/* Sentence case as written (the global .btn rule capitalises words). */
.listing-frozen-dialog .btn {
  text-transform: none;
}
@media (max-width: 575.98px) {
  .listing-frozen-overlay {
    padding: 0;
  }
  .listing-frozen-dialog {
    max-width: 100%;
    max-height: none;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }
}
</style>
